import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

// Safe localStorage helper — private/incognito mode on some browsers blocks it
const safeStorage = {
  get: (key) => { try { return localStorage.getItem(key); } catch { return null; } },
  remove: (key) => { try { localStorage.removeItem(key); } catch {} },
};

// Request interceptor - attach JWT token
api.interceptors.request.use(
  (config) => {
    const token = safeStorage.get("pt_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor - handle 401 (only on actual server 401, not network errors)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Only clear if it's a real 401 from the server (token invalid/expired)
      // Don't clear on network errors, timeouts, etc.
      const isAuthEndpoint = error.config?.url?.includes("/auth/me");
      if (!isAuthEndpoint) {
        // For non-auth endpoints, clear token and redirect
        safeStorage.remove("pt_token");
        safeStorage.remove("pt_user");
        safeStorage.remove("pt_last_activity");
        if (window.location.pathname.startsWith("/admin")) {
          window.location.href = "/admin/login";
        }
      }
      // For /auth/me, let AuthContext handle it (it checks error.response.status)
    }
    return Promise.reject(error);
  },
);

export default api;

// ===== Public API Calls =====
export const publicAPI = {
  getSettings: () => api.get("/settings"),
  getActiveAnnouncements: (params) =>
    api.get("/announcements/active", { params }),
  getDepartments: () => api.get("/departments"),
  getPageSections: (pageSlug, deptId) =>
    api.get(`/page-sections/page/${pageSlug}`, {
      params: deptId ? { department_id: deptId } : {},
    }),
  getCourses: (params) => api.get("/courses", { params }),
  getCourseBySlug: (slug) => api.get(`/courses/slug/${slug}`),
  getFaculty: (params) => api.get("/faculty", { params }),
  getLeadership: () => api.get("/leadership"),
  getPublishedNews: (params) => api.get("/news/published", { params }),
  getNewsBySlug: (slug) => api.get(`/news/slug/${slug}`),
  getGalleries: (params) => api.get("/galleries", { params }),
  getGalleryById: (id) => api.get(`/galleries/${id}`),
  getTestimonials: (params) => api.get("/testimonials", { params }),
  getRecruiters: () => api.get("/recruiters"),
  getMilestones: () => api.get("/milestones"),
  getFacilities: (params) => api.get("/facilities", { params }),
  getWings: () => api.get("/wings"),
  getWingBySlug: (slug) => api.get(`/wings/slug/${slug}`),
  getIncubation: () => api.get("/incubation"),
  getAchievements: (params) => api.get("/achievements", { params }),
  getPlacements: (params) => api.get("/placements", { params }),
  getAlumni: (params) => api.get("/alumni", { params }),
  getAlumniOfficers: (params) => api.get("/alumni-officers", { params }),
  registerAlumni: (data) => api.post("/alumni/register", data),
  uploadAlumniPhoto: (file) => {
    const formData = new FormData();
    formData.append("image", file);
    return api.post("/alumni/upload-photo", formData, { headers: { "Content-Type": "multipart/form-data" } });
  },
  submitEnquiry: (data) => api.post("/enquiries/submit", data),
  getLibrarySections: () => api.get("/library"),
  getLibrarySection: (key) => api.get(`/library/${key}`),
};

// ===== Auth API =====
export const authAPI = {
  login: (data) => api.post("/auth/login", data),
  getMe: () => api.get("/auth/me"),
  changePassword: (data) => api.put("/auth/change-password", data),
};

// ===== Admin API (Generic CRUD) =====
export const adminAPI = {
  // Generic CRUD
  getAll: (resource, params) => api.get(`/admin/${resource}`, { params }),
  getById: (resource, id) => api.get(`/admin/${resource}/${id}`),
  create: (resource, data) => api.post(`/admin/${resource}`, data),
  update: (resource, id, data) => api.put(`/admin/${resource}/${id}`, data),
  remove: (resource, id) => api.delete(`/admin/${resource}/${id}`),
  updateAlumniStatus: (id, status) => api.patch(`/admin/alumni/${id}/status`, { status }),
  reorder: (resource, items) =>
    api.put(`/admin/${resource}/bulk/reorder`, { items }),

  // Dashboard
  getDashboardStats: () => api.get("/admin/dashboard/stats"),

  // Enquiries
  updateEnquiryStatus: (id, data) =>
    api.patch(`/admin/enquiries/${id}/status`, data),

  // Settings
  updateSetting: (key, value) => api.put(`/admin/settings/${key}`, { value }),
  updateSettingsBulk: (settings) =>
    api.put("/admin/settings/bulk", { settings }),

  // Gallery items
  addGalleryItems: (galleryId, items) =>
    api.post(`/admin/galleries/${galleryId}/items`, { items }),
  removeGalleryItem: (itemId) => api.delete(`/admin/galleries/items/${itemId}`),

  // Upload
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append("image", file);
    return api.post("/admin/upload/image", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  uploadImages: (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("images", file));
    return api.post("/admin/upload/images", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  uploadDocument: (file) => {
    const formData = new FormData();
    formData.append("document", file);
    return api.post("/admin/upload/document", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  deleteFile: (fileUrl) =>
    api.delete("/admin/upload/file", { data: { fileUrl } }),

  getLibrarySections: () => api.get("/admin/library"),
  updateLibrarySection: (key, data) => api.put(`/admin/library/${key}`, data),
};
